// src/services/api/entitlementsApi.js
//
// The FE's read of which output types this workspace's plan actually buys.
//   GET /api/v1/entitlements?tenant=<id>
//
// Replaces the dead `balance.allowed_formats` field (BE commit 9dbbbc1,
// 2026-09-20). Contract: <BACKEND_FOLDER>/docs/frontend/api-contract.md §"Entitlements:
// which pairs this plan may run" + ccode/src/api/routes/entitlements.py.
//
// ⛔⛔ ALWAYS send an explicit ?tenant=. Unlike /credits/balance — which answers
// 400 TENANT_REQUIRED without one — this route falls back to the SERVER'S
// CONFIG DEFAULT tenant, and `decide_entitlement` short-circuits the exempt
// tenant (`default`) to EXEMPT: the full ten-variant catalogue. A tenantless
// call therefore does not fail, it SILENTLY UNLOCKS EVERYTHING.
//
// ⚠️ Strict query params: `tenant` is the ONLY legal parameter; anything else
// is 422 UNKNOWN_QUERY_PARAM.
//
// Conventions mirror creditsApi.js: tolerant envelope unwrap, errors carry
// `code` + `status` so callers branch on the code, never on the message.
import { apiFetch } from '../../auth/apiFetch.js';

const ENTITLEMENTS = '/api/v1/entitlements';

async function readError(res) {
  const body = await res.json().catch(() => ({}));
  return body && body.detail && typeof body.detail === 'object' ? body.detail : body;
}

function structuredError(message, envelope, status) {
  const e = new Error(message);
  e.code = envelope?.error_code || '';
  e.status = status;
  return e;
}

/**
 * What this tenant's plan grants for the content-generator application.
 *
 * @param {{tenant: string}} opts REQUIRED — see the header note.
 * @returns {Promise<{application_id: string, plan_id: string|null,
 *   grant_key: string|null, reason: string, features: string[], variants: string[]}>}
 * @throws {Error & {code: string, status: number}} 503 LOOKUP_FAILED is the
 *   one the caller must treat specially — keep the last known state.
 */
export async function fetchEntitlements({ tenant } = {}) {
  if (!tenant) {
    throw structuredError(
      'fetchEntitlements requires an explicit tenant',
      { error_code: 'TENANT_REQUIRED' },
      0
    );
  }
  const params = new URLSearchParams();
  params.set('tenant', tenant);

  const res = await apiFetch(`${ENTITLEMENTS}?${params}`, {
    suppressTenantHeader: true,
  });
  if (!res.ok) {
    const err = await readError(res);
    throw structuredError(
      err.message || `Failed to fetch entitlements: ${res.status}`,
      err,
      res.status
    );
  }
  return res.json();
}

export default fetchEntitlements;
